import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Cost APIs
export const getCostSummary = async () => {
  const response = await api.get('/api/costs/summary');
  return response.data;
};

export const getAWSCosts = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/api/costs/aws', { params });
  return response.data;
};

export const getAzureCosts = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/api/costs/azure', { params });
  return response.data;
};

export const getCostTrend = async (months = 6) => {
  const response = await api.get('/api/costs/trend', { params: { months } });
  return response.data;
};

// Jira APIs
export const getJiraSummary = async () => {
  const response = await api.get('/api/jira/summary');
  return response.data;
};

export const getJiraIssuesByStatus = async () => {
  const response = await api.get('/api/jira/issues-by-status');
  return response.data;
};

export const getJiraIssuesByAssignee = async () => {
  const response = await api.get('/api/jira/issues-by-assignee');
  return response.data;
};

export const getJiraIssuesByPriority = async () => {
  const response = await api.get('/api/jira/issues-by-priority');
  return response.data;
};

export const getJiraRecentActivity = async () => {
  const response = await api.get('/api/jira/recent-activity');
  return response.data;
};

export const getJiraSprint = async () => {
  const response = await api.get('/api/jira/sprint');
  return response.data;
};

export const getJiraVelocity = async () => {
  const response = await api.get('/api/jira/velocity');
  return response.data;
};

export const getJiraRecentlyCreated = async (days = 7) => {
  const response = await api.get('/api/jira/recently-created', { params: { days } });
  return response.data;
};

export const getJiraSprintsList = async () => {
  const response = await api.get('/api/jira/sprints-list');
  return response.data;
};

export const getJiraSprintData = async (sprintId) => {
  const response = await api.get(`/api/jira/sprint-data/${sprintId}`);
  return response.data;
};

export const getJiraIssuesByReporter = async (reporterName) => {
  const response = await api.get(`/api/jira/issues-by-reporter/${encodeURIComponent(reporterName)}`);
  return response.data;
};

// Health check
export const healthCheck = async () => {
  const response = await api.get('/health');
  return response.data;
};

// Credential status check
export const getCredentialsStatus = async () => {
  const response = await api.get('/api/costs/status');
  return response.data;
};

// Service drill-down APIs
export const getAWSServiceDetails = async (serviceName) => {
  const response = await api.get(`/api/costs/aws/service-details/${encodeURIComponent(serviceName)}`);
  return response.data;
};

export const getAzureServiceDetails = async (serviceName) => {
  const response = await api.get(`/api/costs/azure/service-details/${encodeURIComponent(serviceName)}`);
  return response.data;
};

// HubSpot APIs
export const getHubSpotSalesForecast = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/sales-forecast', { params });
  return response.data;
};

export const getHubSpotDeals = async (year, quarter = null, limit = 100) => {
  const params = { limit };
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/deals', { params });
  return response.data;
};

export const getHubSpotDealStages = async () => {
  const response = await api.get('/api/hubspot/deal-stages');
  return response.data;
};

export const getHubSpotWinProbabilityDistribution = async (year, quarter = null, excludeParked = null, excludeLost = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  if (excludeParked !== null) params.exclude_parked = excludeParked;
  if (excludeLost !== null) params.exclude_lost = excludeLost;
  const response = await api.get('/api/hubspot/win-probability-distribution', { params });
  return response.data;
};

export const getHubSpotDealsDetailed = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/deals-detailed', { params });
  return response.data;
};

export const getHubSpotDealStageDuration = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/deal-stage-duration-distribution', { params });
  return response.data;
};

export const getHubSpotDealsByOwner = async (year, quarter = null, excludeParked = null, excludeLost = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  if (excludeParked !== null) params.exclude_parked = excludeParked;
  if (excludeLost !== null) params.exclude_lost = excludeLost;
  const response = await api.get('/api/hubspot/deals-by-owner', { params });
  return response.data;
};

export const getHubSpotACVClosedByOwner = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/acv-closed-by-owner', { params });
  return response.data;
};

export const getHubSpotExpectedACVByOwner = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/expected-acv-by-owner', { params });
  return response.data;
};

export const getHubSpotWeightedTCVByStage = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/weighted-tcv-by-stage', { params });
  return response.data;
};

export const getHubSpotWeightedACVByStage = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/weighted-acv-by-stage', { params });
  return response.data;
};

export const getHubSpotFunnelAge = async (year) => {
  const params = {};
  if (year) params.year = year;
  const response = await api.get('/api/hubspot/funnel-age', { params });
  return response.data;
};

export const getHubSpotQuarterlyComparison = async (year) => {
  const params = {};
  if (year) params.year = year;
  const response = await api.get('/api/hubspot/quarterly-comparison', { params });
  return response.data;
};

export const getHubSpotDealsByStageAndOwner = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/deals-by-stage-and-owner', { params });
  return response.data;
};

export const getHubSpotDealsFiltered = async (year, quarter = null, stage = null, owner = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  if (stage) params.stage = stage;
  if (owner) params.owner = owner;
  const response = await api.get('/api/hubspot/deals-filtered', { params });
  return response.data;
};

export const getHubSpotDealCountAllStages = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/deal-count-all-stages', { params });
  return response.data;
};

export const getHubSpotDealsBoard = async (year) => {
  const params = {};
  if (year) params.year = year;
  const response = await api.get('/api/hubspot/deals-board', { params });
  return response.data;
};

export const getHubSpotWeeklyDeals = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/api/hubspot/deals-detailed-weekly', { params });
  return response.data;
};

export const getHubSpotDealCountByStage = async (year, quarter = null) => {
  const params = {};
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  const response = await api.get('/api/hubspot/deal-count-by-stage', { params });
  return response.data;
};

// Revenue APIs
export const uploadRevenue = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/api/revenue/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  });
  return response.data;
};

export const getQuarterlyRevenue = async () => {
  const response = await api.get('/api/revenue/quarterly-revenue');
  return response.data;
};

export const getRevenueTables = async () => {
  const response = await api.get('/api/revenue/tables');
  return response.data;
};

export const getRevenueData = async (tableName) => {
  const response = await api.get(`/api/revenue/data/${tableName}`);
  return response.data;
};

export default api;
